import React, { useMemo } from "react";
import { find, map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import Panel from "@ui-kit/popover/panel";
import Boolean from "@radarly/components/filterbar/panels/boolean";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty, isFlagClause } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";
import { getNextClauseFromFlagValue } from "./clause";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

function getFlagValue(facet: FullFacet, clause: Clause) {
  if (clause && clause.and) {
    const flagClause = find(clause.and, isFlagClause);
    return flagClause ? (flagClause[facet.id] as boolean) : null;
  } else {
    return null;
  }
}

const getLabel = (facet: FullFacet, index: number, locale: Locale) => {
  if (facet.id === Facet.Favorite) {
    return index === 0
      ? tr(locale, "FACETS::FLAG::FAVORITE::VALUES::TRUE")
      : tr(locale, "FACETS::FLAG::FAVORITE::VALUES::FALSE");
  }
  if (facet.id === Facet.TwitterCertified) {
    return index === 0
      ? tr(locale, "FACETS::FLAG::CERTIFIED::VALUES::TRUE")
      : tr(locale, "FACETS::FLAG::CERTIFIED::VALUES::FALSE");
  }
  return facet.values[index].label;
};
const RadarlyFilterbarPanelsBoolean = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const onSetFlag = (flag: boolean) => {
    setClause(getNextClauseFromFlagValue(facet, flag));
  };
  const flag = useMemo(() => getFlagValue(facet, clause), [clause, facet]);
  const flags = useMemo(() => {
    return map(facet.values, (value, i) => {
      return {
        id: `selected_${value.id}`,
        label: getLabel(facet, i, locale),
        flag: !!value.id,
      };
    });
  }, [facet, locale]);

  const onClear = () =>
    setClause(refineClauseWithInnerClause(clause, { [facet.id]: null }, true));

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <Boolean flags={flags} onSetFlag={onSetFlag} flag={flag} />
    </Panel>
  );
};

export default RadarlyFilterbarPanelsBoolean;
